import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useMembership } from "@/hooks/use-membership";
import { useOrganizationLLMUsage } from "@/queries/ona-intelligence-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { type FC, useState } from "react";
import { UsageSummaryCards } from "@/routes/settings/organization/ona-intelligence/UsageSummaryCards";
import { UsageChart } from "@/routes/settings/organization/ona-intelligence/UsageChart";
import { TimeRangeSelector } from "@/components/TimeRangeSelector";
import { TIME_RANGES, type TimeRange } from "@/routes/insights/insights";
import { Banner } from "@/components/flexkit/Banner";
import { Text } from "@/components/podkit/typography/Text";
import { Code, ConnectError } from "@connectrpc/connect";
import { timeAgo, formatShortDate } from "@/format/time";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";

const OnaIntelligencePage: FC = () => {
    useDocumentTitle("Ona Intelligence Usage");

    const timeRanges = TIME_RANGES.filter((r) =>
        ["last-week", "last-month", "last-year"].includes(r.value),
    );

    const [selectedRange, setSelectedRange] = useState<TimeRange>(timeRanges[0]);
    const { membership, isPending: isLoadingMembership } = useMembership();
    const { data: usageData, isLoading: isLoadingUsage, error } = useOrganizationLLMUsage();

    if (!membership && isLoadingMembership) {
        return <LoadingState />;
    }

    if (membership?.userRole !== OrganizationRole.ADMIN) {
        return <NotAuthorized />;
    }

    if (isLoadingUsage) {
        return <LoadingState />;
    }

    if (error instanceof ConnectError && error.code === Code.NotFound) {
        return (
            <Banner
                variant="info"
                data-testid="ona-intelligence-not-enabled"
                className="mt-6 max-w-[550px]"
                text={
                    <div className="pl-2">
                        <Text>Ona Intelligence is not enabled for your organization.</Text>
                        <Text>Contact your account representative to enable this feature.</Text>
                    </div>
                }
            />
        );
    }

    if (error) {
        return (
            <Banner
                variant="danger"
                data-testid="usage-error"
                className="mt-6 max-w-[550px]"
                text={
                    <div className="pl-2">
                        <Text>Failed to load usage data. Please try again later.</Text>
                    </div>
                }
            />
        );
    }

    if (!usageData) {
        return null;
    }

    let selectedSummary;
    let period: "week" | "month" | "year";
    if (selectedRange.days <= 7) {
        selectedSummary = usageData.thisWeek;
        period = "week";
    } else if (selectedRange.days <= 30) {
        selectedSummary = usageData.thisMonth;
        period = "month";
    } else {
        selectedSummary = usageData.thisYear;
        period = "year";
    }

    const lastUpdated = usageData.lastCalculatedAt
        ? timeAgo(new Date(Number(usageData.lastCalculatedAt.seconds) * 1000))
        : "recently";

    const trackingSince = usageData.usageDataFrom
        ? formatShortDate(new Date(Number(usageData.usageDataFrom.seconds) * 1000), true)
        : "N/A";

    return (
        <div className="flex max-w-[820px] flex-col gap-6">
            <div className="text-sm text-content-strong">
                View your Ona Intelligence credit usage and track consumption over time.
            </div>

            {/* Time Range Selector (aligned with Insights) */}
            <div className="max-w-[220px]">
                <TimeRangeSelector
                    timeRanges={timeRanges}
                    selectedRange={selectedRange}
                    setSelectedRange={setSelectedRange}
                />
            </div>

            {/* Summary Cards */}
            {selectedSummary && <UsageSummaryCards summary={selectedSummary} period={period} />}

            {/* Usage Chart */}
            {selectedSummary?.dailyBreakdown && selectedSummary.dailyBreakdown.length > 0 ? (
                <div className="flex flex-col gap-3">
                    <UsageChart dailyBreakdown={selectedSummary.dailyBreakdown} animationKey={selectedRange.value} />
                    {/* Metadata under chart */}
                    <div className="flex gap-6 text-sm text-content-secondary">
                        <div>
                            <span className="font-medium">Tracking since:</span> {trackingSince}
                        </div>
                        <div>
                            <span className="font-medium">Last updated:</span> {lastUpdated}
                        </div>
                    </div>
                </div>
            ) : (
                <EmptyStateCard
                    data-testid="no-usage-data"
                    title="No usage data yet"
                    description="When your organization starts using Ona Intelligence, daily credit usage will appear here."
                />
            )}

            {/* Disclaimer Banner (moved to bottom) */}
            <Banner
                variant="info"
                data-testid="usage-disclaimer"
                className="text-base"
                text={
                    <div className="pl-2">
                        <Text className="font-semibold">This data is informational only</Text>
                        <Text>Please contact your Account Manager for precise billing information.</Text>
                    </div>
                }
            />
        </div>
    );
};

export default OnaIntelligencePage;
